import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation files
const translations = {
  en: {
    // Header
    helpContact: 'Help & Contact',
    login: 'Login',
    logout: 'Logout',
    admin: 'Admin',
    cart: 'Shopping Cart',
    search: 'Search',
    searchResults: 'Search Results',
    resultsFor: 'results for',
    noResultsFor: 'No results for',
    enterSearchQuery: 'Enter a search term above to find products.',
    
    // Navigation
    men: 'Men',
    women: 'Women',
    mxGear: 'MX Gear',
    accessories: 'Accessories',
    graphics: 'Graphics',

    // Subcategories - Men
    hoodiesSweaters: 'Hoodies & Sweaters',
    tshirts: 'T-shirts',
    jackets: 'Jackets',
    pants: 'Pants',
    shorts: 'Shorts',
    underwear: 'Underwear',

    // Subcategories - Women
    sportsBrasBodysuits: 'Sports Bras & Bodysuits',
    womenHoodies: 'Hoodies',
    leggings: 'Leggings',
    sweatpants: 'Sweatpants',

    // Subcategories - MX Gear
    mxGloves: 'Gloves',
    mxGoggles: 'MX Goggles',
    mxPants: 'MX Pants',
    mxJerseys: 'MX Jerseys',
    helmets: 'Helmets',
    bags: 'Bags',
    protectors: 'Protectors',
    
    // Hero
    heroTitle: 'Premium Gear for Riders',
    heroSubtitle: 'Discover our collection of high-quality apparel and accessories',
    shopMen: 'Shop Men',
    shopWomen: 'Shop Women',
    
    // Common
    addToCart: 'Add to Cart',
    price: 'Price',
    size: 'Size',
    quantity: 'Quantity',
    pleaseSelectSize: 'Please select a size',
    total: 'Total',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    free: 'Free',
    proceedToCheckout: 'Proceed to Checkout',
    continueShopping: 'Continue Shopping',
    remove: 'Remove',
    emptyCart: 'Your cart is empty',
    
    // Footer
    about: 'About',
    contact: 'Contact',
    faq: 'FAQ',
    terms: 'Terms',
    privacy: 'Privacy',
    newsletter: 'Newsletter',
    refundPolicy: 'Refund Policy',
    imprint: 'Imprint',
    payment: 'Payment',
    returns: 'Returns',
    phone: 'Phone',
    email: 'Email',
    businessHours: 'Business Hours',
    newsletterText: 'Subscribe to our newsletter to get the latest updates, exclusive offers, and new product announcements.',
    newsletterPageSubtitle: 'Stay updated with our latest products, exclusive offers, and news.',
    whySubscribe: 'Why Subscribe?',
    newsletterBenefit1: 'Get exclusive discounts and early access to sales',
    newsletterBenefit2: 'Be the first to know about new product launches',
    newsletterBenefit3: 'Receive styling tips and rider community updates',
    newsletterBenefit4: 'Special offers only available to subscribers',
    subscribeNow: 'Subscribe Now',
    newsletterPrivacy: 'We respect your privacy. You can unsubscribe at any time. We will never share your email address with third parties.',
    emailPlaceholder: 'Enter your email address',
    subscribe: 'Subscribe',
    newsletterSuccess: 'Thank you for subscribing to our newsletter!',
    learnMoreNewsletter: 'Learn more about our newsletter',
    
    // Graphics Page
    graphicsTitle: 'Custom Motorcycle Graphics Design',
    graphicsSubtitle: 'Transform your bike with personalized graphics designed just for you',
    readyToStart: 'Ready to Get Started?',
    requestQuote: 'Request a Quote',
    closeForm: 'Close Form',
    contactUsToday: 'Contact us today to discuss your custom motorcycle graphics project',
    graphicsDescription: 'At Riders Forge, we specialize in creating custom motorcycle graphics that reflect your unique style and personality. Whether you\'re looking for racing stripes, custom logos, full body wraps, or personalized decals, our design team can bring your vision to life.',
    customDesign: 'Custom Design',
    customDesignDesc: 'Work with our designers to create graphics that match your style and preferences',
    bikeSpecific: 'Bike-Specific',
    bikeSpecificDesc: 'Designs tailored to your specific motorcycle model and year',
    preciseFit: 'Precise Fit',
    preciseFitDesc: 'Perfect fit guaranteed with our precise measurements and templates',
    premiumQuality: 'Premium Quality',
    premiumQualityDesc: 'High-quality vinyl materials that are durable and weather-resistant',
    ourDesignProcess: 'Our Design Process',
    consultation: 'Consultation',
    consultationDesc: 'Contact us with your ideas, bike model, and design preferences',
    design: 'Design',
    designDesc: 'Our team creates custom mockups based on your requirements',
    reviewRevise: 'Review & Revise',
    reviewReviseDesc: 'Review the designs and request any changes until it\'s perfect',
    production: 'Production',
    productionDesc: 'Once approved, we print and cut your graphics with precision',
    installation: 'Installation',
    installationDesc: 'Receive your graphics ready to install, or we can help with installation',
    graphicsFormTitle: 'Graphics Design Request Form',
    fullName: 'Full Name',
    emailLabel: 'Email',
    phoneNumber: 'Phone Number',
    bikeModel: 'Bike Model',
    bikeYear: 'Bike Year',
    designType: 'Design Type',
    selectDesignType: 'Select design type',
    fullBodyWrap: 'Full Body Wrap',
    racingStripes: 'Racing Stripes',
    customLogos: 'Custom Logos',
    numberPlates: 'Number Plates',
    decalsStickers: 'Decals & Stickers',
    other: 'Other',
    designDescription: 'Design Description',
    designDescriptionPlaceholder: 'Describe your design ideas, colors, style preferences, etc...',
    budgetRange: 'Budget Range',
    selectBudget: 'Select budget',
    under200: 'Under €200',
    budget200500: '€200 - €500',
    budget5001000: '€500 - €1,000',
    over1000: 'Over €1,000',
    timeline: 'Timeline',
    selectTimeline: 'Select timeline',
    asap: 'ASAP',
    weeks12: '1-2 weeks',
    month1: '1 month',
    flexible: 'Flexible',
    submitRequest: 'Submit Request',
    thankYouMessage: 'Thank you! We will contact you soon to discuss your motorcycle graphics project.',
    namePlaceholder: 'John Doe',
    phonePlaceholder: '+421 912 123 456',
    bikeModelPlaceholder: 'e.g., Yamaha YZ450F',
    bikeYearPlaceholder: 'e.g., 2023',
    
    // Cart Page
    shoppingCart: 'Shopping Cart',
    yourCartIsEmpty: 'Your cart is empty',
    returnToCart: 'Return to Cart',
    pleaseFillAllFields: 'Please fill in all required fields.',
    startShopping: 'Start shopping to add items to your cart',
    orderSummary: 'Order Summary',
    itemSubtotal: 'Item Subtotal',
    freeShippingAt: 'Add {amount} more for free shipping',
    freeShippingNote: 'You qualify for free shipping!',
    maximumInStock: 'Maximum in stock',
    
    // Checkout Page
    checkout: 'Checkout',
    contactInformation: 'Contact Information',
    shippingAddress: 'Shipping Address',
    paymentMethod: 'Payment Method',
    paymentMethodUpdated: 'Payment method updated successfully!',
    defaultPaymentMethod: 'Default Payment Method',
    changePaymentMethod: 'Change Payment Method',
    creditDebitCard: 'Credit/Debit Card',
    paypal: 'PayPal',
    bankTransfer: 'Bank Transfer',
    firstName: 'First Name',
    lastName: 'Last Name',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal Code',
    country: 'Country',
    placeOrder: 'Place Order',
    placeOrderError: 'Failed to place order. Please try again.',
    placingOrder: 'Placing Order...',
    processing: 'Processing...',
    
    // Product Detail
    description: 'Description',
    shippingReturns: 'Shipping & Returns',
    shippingReturnsText: 'Free shipping on orders over €50. Easy returns within 30 days.',
    selectSize: 'Please select a size',
    addedToCart: 'Added to Cart!',
    
    // Category Page
    productsFound: '{count} product{plural} found',
    noProductsFound: 'No products found in this category.',
    
    // Common buttons
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    close: 'Close',
    outOfStock: 'Out of Stock',
    allInCart: 'All in cart',
    availableToAdd: 'available to add',
    alreadyInCart: 'already in cart',
    inSize: 'in',
    thisSizeOutOfStock: 'This size is out of stock',
    noMoreInStockForSize: 'No more in stock for this size.',
    onlyXMoreAvailable: 'Only {available} more available for this size ({stock} in stock, {inCart} in cart).',
    notSpecified: 'Not specified',
    backToAccount: 'Back to Account',
    loadingOrderDetails: 'Loading order details...',
    orderNotFound: 'Order not found',
    noPermissionViewOrder: 'You do not have permission to view this order.',
    failedToLoadOrder: 'Failed to load order details.',
    
    // Account
    myAccount: 'My Account',
    profile: 'Profile',
    orderHistory: 'Order History',
    addresses: 'Addresses',
    paymentMethods: 'Payment Methods',
    
    // Login
    createAccount: 'Create Account',
    welcomeBack: 'Welcome back! Please login to your account.',
    createAccountSubtitle: 'Create an account to track orders and save your preferences.',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    register: 'Register',
    signUp: 'Sign Up',
    noAccount: "Don't have an account? ",
    haveAccount: 'Already have an account? ',
    firstNamePlaceholder: 'John',
    lastNamePlaceholder: 'Doe',
    password: 'Password',
    pleaseProvideName: 'Please provide first and last name',
    emailAlreadySignedIn: 'This email has been signed in. Please use a different email or try logging in.',
    invalidCredentials: 'Invalid email or password. Please check your credentials and try again.',
    errorOccurred: 'An error occurred. Please try again.',
    checkYourEmail: 'Check your email',
    resetEmailSent: "If an account exists for that email, we've sent instructions to reset your password.",
    recoveryCodeBelow: 'Your recovery code is in the simulated notification below.',
    passwordResetEmail: 'Password reset',
    yourRecoveryCode: 'Your password recovery code',
    recoveryCodeMessage: 'Use this code to reset your password:',
    recoveryCodeHint: 'Enter this code on the next screen along with your new password.',
    continueToResetPassword: 'Continue to reset password',
    noMessageForEmail: 'No password reset request found for this email address.',
    forgotPasswordSubtitle: "Enter your email and we'll show you a recovery code in a simulated email notification.",
    sendRecoveryCode: 'Send recovery code',
    backToLogin: 'Back to login',
    resetPassword: 'Reset password',
    resetPasswordSubtitle: 'Enter the recovery code and your new password below.',
    recoveryCode: 'Recovery code',
    recoveryCodePlaceholder: 'Paste the code from the email',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    passwordsDoNotMatch: 'Passwords do not match.',
    passwordMinLength: 'Password must be at least 6 characters.',
    recoveryCodeRequired: 'Please enter the recovery code from the email.',
    requestNewCode: 'Request new recovery code',
    passwordUpdated: 'Password updated. You can log in now.',
    invalidResetLink: 'Invalid reset link. Please use the link from your email.',
    invalidResetLinkSubtitle: 'This link is missing or invalid. Please request a new password reset from the login page.',
    requestNewLink: 'Request new link',
    inStock: 'in stock',
    quantityPerSize: 'Quantity per Size',
    quantityPerSizeHint: 'Set quantity for each size. Total quantity will be calculated automatically.',
    
    // Footer
    followUs: 'Follow Us',
    customerService: 'Customer Service',
    information: 'Information',
    allRightsReserved: 'All rights reserved',
    imageCredit: 'Product images',
    imageCreditFrom: 'from',
    
    // Product Detail
    productNotFound: 'Product not found',
    goToHome: 'Go to Home',
    saveAmount: 'Save €{amount}',
    
    // Account Page
    pleaseLogin: 'Please log in to view your account.',
    profileUpdated: 'Profile updated successfully!',
    updateFailed: 'Failed to update profile. Please try again.',
    profileInformation: 'Profile Information',
    saveChanges: 'Save Changes',
    noOrdersYet: 'No orders yet.',
    order: 'Order',
    viewDetails: 'View Details',
    savedAddresses: 'Saved Addresses',
    defaultAddress: 'Default Address',
    edit: 'Edit',
    addNewAddress: 'Add New Address',
    noSavedPaymentMethods: 'No saved payment methods.',
    addPaymentMethod: 'Add Payment Method',
    
    // Home Page
    highlights: 'Highlights',
    allProducts: 'All Products',
    loadingProducts: 'Loading products...',
    
    // Admin Dashboard
    adminDashboard: 'Admin Dashboard',
    adminWelcomeBack: 'Welcome back, {name}',
    overview: 'Overview',
    products: 'Products',
    orders: 'Orders',
    customers: 'Customers',
    settings: 'Settings',
    loadingDashboardData: 'Loading dashboard data...',
    failedToLoadDashboard: 'Failed to load dashboard data. Please check if the backend is running.',
    totalOrders: 'Total Orders',
    totalRevenue: 'Total Revenue',
    totalProducts: 'Total Products',
    totalCustomers: 'Total Customers',
    pendingOrders: 'Pending Orders',
    completedOrders: 'Completed Orders',
    location: 'Location',
    totalSpent: 'Total Spent',
    registeredDate: 'Registered',
    active: 'Active',
    inactive: 'Inactive',
    noCustomers: 'No customers found.',
    orderDetails: 'Order Details',
    orderStatus: 'Order Status',
    orderDate: 'Order Date',
    totalAmount: 'Total Amount',
    noItems: 'No items found',
    noItemsInOrder: 'No items in this order.',
    customerInformation: 'Customer Information',
    orderInformation: 'Order Information',
    orderItems: 'Order Items',
    updateStatusError: 'Failed to update order status. Please try again.',
    pending: 'Pending',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    items: 'Items',
    product: 'Product',
    productId: 'Product ID',
    unitPrice: 'Unit Price',
    itemsTotal: 'Items Total',
    orderTotal: 'Order Total',
    unknownProduct: 'Unknown Product',
    recentOrders: 'Recent Orders',
    orderId: 'Order ID',
    customer: 'Customer',
    amount: 'Amount',
    status: 'Status',
    date: 'Date',
    actions: 'Actions',
    view: 'View',
    noRecentOrders: 'No recent orders found.',
    noOrders: 'No orders found.',
    contactRequests: 'Contact Requests',
    graphicsRequests: 'Graphics Requests',
    noContactRequests: 'No contact requests found.',
    noGraphicsRequests: 'No graphics requests found.',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    sending: 'Sending...',
    submitting: 'Submitting...',
    messageSentError: 'Failed to send message. Please try again.',
    requestError: 'Failed to submit request. Please try again.',
    contactRequestDetails: 'Contact Request Details',
    graphicsRequestDetails: 'Graphics Request Details',
    messageDetails: 'Message Details',
    bikeInformation: 'Bike Information',
    projectDetails: 'Project Details',
    addressUpdated: 'Address updated successfully!',
    noAddressSaved: 'No address saved yet. Click Edit to add one.',
    enterAddress: 'Enter your address',
    enterCity: 'Enter your city',
    enterPostalCode: 'Enter postal code',
    saving: 'Saving...',
    productsManagement: 'Products Management',
    addNewProduct: '+ Add New Product',
    subcategory: 'Subcategory',
    productsManagementComingSoon: 'Product management interface coming soon...',
    productName: 'Product Name',
    productNamePlaceholder: 'Enter product name',
    regularPrice: 'Regular Price',
    optional: 'Optional',
    sizesPlaceholder: 'S, M, L, XL, XXL',
    sizesHint: 'Enter sizes separated by commas (e.g., S, M, L, XL)',
    imageUrl: 'Image URL',
    imageUrlPlaceholder: 'https://example.com/image.jpg',
    imageUrlHint: 'Leave empty to use a placeholder image',
    markAsNew: 'Mark as New Product',
    createProduct: 'Create Product',
    creating: 'Creating...',
    productCreatedSuccess: 'Product created successfully!',
    editProduct: 'Edit Product',
    updateProduct: 'Update Product',
    updating: 'Updating...',
    productUpdatedSuccess: 'Product updated successfully!',
    delete: 'Delete',
    confirmDeleteProduct: 'Are you sure you want to delete this product?',
    deleteProductError: 'Failed to delete product. Please try again.',
    noProducts: 'No products found.',
    image: 'Image',
    loading: 'Loading...',
    clickToZoom: 'Click to zoom',
    ordersManagement: 'Orders Management',
    ordersManagementComingSoon: 'Orders management interface coming soon...',
    customersManagement: 'Customers Management',
    customersManagementComingSoon: 'Customers management interface coming soon...',
    settingsComingSoon: 'Settings interface coming soon...',
    
    // Contact Page
    contactUs: 'Contact Us',
    contactSubtitle: 'Have a question or need help? We\'re here for you!',
    getInTouch: 'Get in Touch',
    contactSupportText: 'Our customer support team is available to help you with any questions or concerns.',
    phoneWhatsapp: 'Phone & WhatsApp',
    contactHours: 'Monday to Friday, 09:00 to 16:00',
    respondWithin24: 'We respond within 24 hours',
    support: 'Support',
    shippingDelivery: 'Shipping & Delivery',
    shippingPageSubtitle: 'Everything you need to know about delivery.',
    shippingOptions: 'Shipping Options',
    shippingOptionsText: 'We offer standard and express shipping. Choose the option that best fits your needs at checkout.',
    standardShipping: 'Standard Shipping',
    standardShippingDesc: '5–7 business days within Europe, 7–14 days internationally. €5.99 or free on orders over €50.',
    expressShipping: 'Express Shipping',
    expressShippingDesc: '2–3 business days. Available at checkout for an additional fee.',
    freeShipping: 'Free Shipping',
    freeShippingText: 'Free standard shipping on all orders over €50. No code needed – your order automatically qualifies at checkout.',
    orderProcessing: 'Order Processing',
    orderProcessingText: 'Orders are typically processed within 1–2 business days. You will receive an email confirmation with tracking information once your order has shipped.',
    internationalShipping: 'International Shipping',
    internationalShippingText: 'We ship worldwide to over 80 countries. Delivery times and customs may apply depending on your location. You are responsible for any import duties or taxes in your country.',
    trackingYourOrder: 'Tracking Your Order',
    trackingText: 'After your order ships, you will receive a tracking link by email. You can also view the status of your order in your account under Order History.',
    shippingContactText: 'Questions about shipping? Contact us at support@grenzgaenger-shop.com or visit our Contact page.',
    sendUsMessage: 'Send us a Message',
    name: 'Name',
    subject: 'Subject',
    message: 'Message',
    sendMessage: 'Send Message',
    messageSentSuccess: 'Message sent successfully!',
    
    // FAQ Page
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    faqSubtitle: 'Find answers to common questions about our products, shipping, returns, and more.',
    questionsAboutStore: 'Questions about the store',
    stillHaveQuestions: 'Still have questions?',
    cantFindAnswer: 'Can\'t find the answer you\'re looking for? Please feel free to contact us.',
    contactUsBtn: 'Contact Us',
    faq1Question: 'Where does Riders Forge ship to?',
    faq1Answer: 'Riders Forge ships worldwide! We deliver to almost every country, connecting riders globally with premium gear and apparel. Whether you\'re in Europe, the Americas, Asia, or anywhere else, we\'ve got you covered. Standard shipping typically takes 5-7 business days within Europe and 7-14 business days internationally. Express shipping options are available at checkout for faster delivery.',
    faq2Question: 'How can I contact you if I have a request or a question?',
    faq2Answer: 'For inquiries by email, you can reach our customer support team at support@ridersforge.com. You can also reach us from Monday - Friday (except on public holidays) from 09:00 - 16:00 by phone or WhatsApp at +421 912 123 456. Our AI chatbot is also available 24/7 for instant answers to common questions - just click the chat button in the bottom right corner!',
    faq3Question: 'Do you have a physical store?',
    faq3Answer: 'We are an online store specializing in premium gear for riders. While we don\'t currently have a physical store location, we occasionally host pop-up events where you can see and try our products in person. Stay updated on upcoming events through our newsletter and social media channels.',
    faq4Question: 'What is your return policy?',
    faq4Answer: 'We offer a 30-day return policy. Items must be unworn, unwashed, and in their original packaging with tags attached. You can initiate a return through your account dashboard or by contacting us at support@ridersforge.com. Once we receive your return, we\'ll process your refund within 5-7 business days.',
    faq5Question: 'How long does shipping take?',
    faq5Answer: 'Standard shipping typically takes 3-7 business days within Europe, and 7-14 business days for international orders. Express shipping options are available at checkout for faster delivery. Free shipping is available on orders over €50.',
    faq6Question: 'What payment methods do you accept?',
    faq6Answer: 'We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers. All payments are processed securely through our encrypted payment gateway.',
    faq7Question: 'Can I track my order?',
    faq7Answer: 'Yes! Once your order has been shipped, you will receive a tracking number via email. You can use this number to track your package on our website or the carrier\'s website.',
    faq8Question: 'Do you offer gift cards?',
    faq8Answer: 'Currently, we do not offer physical or digital gift cards. However, you can purchase items as gifts and we can include a personalized message with your order. Please add gift message details at checkout.',
    
    // Chatbot
    chatbotWelcome: "Hello! I'm your AI support assistant. How can I help you today?",
    chatbotTitle: 'AI Support Assistant',
    chatbotOnline: 'Online',
    chatbotPlaceholder: 'Type your message...',
    chatbotCloseLabel: 'Close chatbot',
    chatbotGreetingReply: "Hello! I'm here to help you with any questions about Riders Forge. What would you like to know?",
    chatbotShipping: 'We offer standard shipping (5-7 business days) and express shipping (2-3 business days). Free shipping is available on orders over €50. Orders are typically processed within 1-2 business days.',
    chatbotReturns: 'We offer a 30-day return policy. Items must be unworn, unwashed, and in original packaging. You can initiate a return through your account or contact our support team at support@ridersforge.com.',
    chatbotPayment: 'We accept credit/debit cards, PayPal, and bank transfers. All payments are processed securely. Your payment information is encrypted and never stored on our servers.',
    chatbotProducts: 'You can browse our products by category (Men, Women, MX Gear, Accessories). Each product page shows available sizes and stock status. If you need help finding a specific item, let me know!',
    chatbotOrderTracking: "You can track your order status in your account dashboard. Once your order ships, you'll receive a tracking number via email. If you need help, contact support@ridersforge.com.",
    chatbotSizing: "Size charts are available on each product page. If you're unsure about sizing, we recommend checking the size guide or contacting our support team for personalized assistance.",
    chatbotContact: 'You can reach us at:\n• Phone: +421 912 123 456\n• Email: support@ridersforge.com\n• Hours: Mon-Fri 09:00 - 16:00\nYou can also visit our Contact page for more options.',
    chatbotNavigate: 'You can browse products by category in the main menu: Men, Women, MX Gear and Accessories or check out our featured products on the homepage.',
    chatbotAccount: "You can create an account by clicking 'Login' in the top right corner, then selecting 'Sign Up'. With an account, you can track orders, save addresses, and manage your preferences.",
    chatbotHelp: "I'm here to help! You can ask me about:\n• Shipping and delivery\n• Returns and refunds\n• Product information\n• Order tracking\n• Payment methods\n• Size guides\n• Or anything else about Riders Forge!",
    chatbotCommandsList: 'Here are the topics I understand (you can write in English or Slovak):\n\n• Shipping – shipping, delivery, doprava, doručenie\n• Returns – return, refund, vrátenie, reklamácia\n• Payment – payment, pay, platba, karta\n• Products – product, stock, produkt, sklad\n• Order tracking – order, track, objednávka, sledovanie\n• Sizes – size, sizing, veľkosť, meranie\n• Contact – contact, support, kontakt, telefón\n• Navigation – menu, categories, ponuka, kategórie\n• Account – account, login, účet, registrácia\n• Help – help, ?, príkazy, pomoc\n\nJust type a word from any topic and I\'ll answer!',
    chatbotDefaultReply: 'I understand you\'re asking about: "{message}". Could you provide more details? You can also contact our support team at support@ridersforge.com for immediate assistance.',
    chatbotButtonTitle: 'Need help? Chat with us!',
    
    // About Page
    aboutRidersForge: 'About Riders Forge',
    premiumGearForRiders: 'Premium Gear for Riders',
    ourStory: 'Our Story',
    ourStoryText1: 'Riders Forge was born from a passion for motorcycling and the desire to create high-quality gear that combines style, functionality, and durability. Founded with the mission of providing premium gear for riders, we\'ve been serving the global motorcycle community with dedication and excellence.',
    ourStoryText2: 'What started as a small operation has grown into an international brand, shipping products worldwide. Our commitment to quality and customer satisfaction has made us a trusted name in motorcycle apparel and accessories.',
    ourMission: 'Our Mission',
    ourMissionText1: 'At Riders Forge, we believe that every rider deserves gear that not only looks great but also performs at the highest level. We design and curate products that meet the demands of modern riders while maintaining timeless style and superior quality.',
    ourMissionText2: 'Our mission is to provide riders with premium gear that enhances their riding experience, whether they\'re on the track, trail, or street. We\'re committed to quality, innovation, and the rider community.',
    whatWeOffer: 'What We Offer',
    globalShipping: 'Global Shipping',
    globalShippingDesc: 'We ship worldwide to over 80 countries, bringing our products directly to riders everywhere.',
    communityDriven: 'Community Driven',
    communityDrivenDesc: 'Our community of riders is at the heart of everything we do. Your feedback shapes our products.',
    customerFirst: 'Customer First',
    customerFirstDesc: 'Exceptional customer service is our priority. We\'re here to help you find the perfect gear.',
    joinOurCommunity: 'Join Our Community',
    joinCommunityText: 'Follow us on social media to stay connected with the Riders Forge community, get the latest updates on new products, and share your riding adventures with fellow riders around the world.',
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
    youtube: 'YouTube',
    
    // Terms Page
    termsOfService: 'Terms of Service',
    lastUpdated: 'Last updated: January 2024',
    acceptanceOfTerms: '1. Acceptance of Terms',
    acceptanceText: 'By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.',
    useLicense: '2. Use License',
    useLicenseText: 'Permission is granted to temporarily download one copy of the materials on Riders Forge\'s website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:',
    modifyCopy: 'modify or copy the materials',
    commercialUse: 'use the materials for any commercial purpose or for any public display',
    decompile: 'attempt to decompile or reverse engineer any software contained on the website',
    removeCopyright: 'remove any copyright or other proprietary notations from the materials',
    productsPricing: '3. Products and Pricing',
    productsPricingText: 'We reserve the right to modify product prices at any time without prior notice. All prices are in EUR unless otherwise stated. Product images are for illustrative purposes only and may differ from the actual product. We strive to ensure product information is accurate, but we do not warrant that product descriptions or other content is error-free.',
    ordersPayment: '4. Orders and Payment',
    ordersPaymentText: 'When you place an order, you are making an offer to purchase products. We reserve the right to accept or decline your order. Payment must be received before we process your order. We accept major credit cards, PayPal, and bank transfers.',
    shippingDeliveryTerms: '5. Shipping and Delivery',
    shippingDeliveryText: 'We ship worldwide to over 80 countries. Shipping times vary by location. We are not responsible for delays caused by customs, weather, or other circumstances beyond our control. Risk of loss and title for products pass to you upon delivery to the carrier.',
    returnsRefunds: '6. Returns and Refunds',
    returnsRefundsText: 'You may return items within 30 days of purchase for a full refund, provided they are in their original condition with tags attached. Return shipping costs are the responsibility of the customer, unless the item is defective or we made an error.',
    limitationLiability: '7. Limitation of Liability',
    limitationLiabilityText: 'Riders Forge shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.',
    privacyPolicyTerms: '8. Privacy Policy',
    privacyPolicyTermsText: 'Your use of this website is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices.',
    contactInformationText: 'If you have any questions about these Terms of Service, please contact us at support@ridersforge.com or call +421 912 123 456.',
    
    // Privacy Page
    privacyPolicy: 'Privacy Policy',
    introduction: '1. Introduction',
    introductionText: 'At Riders Forge, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.',
    informationWeCollect: '2. Information We Collect',
    personalInformation: '2.1 Personal Information',
    personalInfoText: 'We collect personal information that you provide to us, including:',
    nameContact: 'Name and contact information (email address, phone number, mailing address)',
    paymentInfo: 'Payment information (processed securely through our payment providers)',
    accountCredentials: 'Account credentials if you create an account',
    communicationPrefs: 'Communication preferences',
    automaticallyCollected: '2.2 Automatically Collected Information',
    automaticallyCollectedText: 'When you visit our website, we automatically collect:',
    ipDevice: 'IP address and device information',
    browserType: 'Browser type and version',
    pagesVisited: 'Pages visited and time spent on pages',
    referralUrls: 'Referral URLs',
    cookiesTracking: 'Cookies and similar tracking technologies',
    howWeUse: '3. How We Use Your Information',
    howWeUseText: 'We use the collected information for various purposes:',
    processOrders: 'To process and fulfill your orders',
    communicateOrders: 'To communicate with you about your orders, products, and services',
    improveWebsite: 'To improve our website and services',
    marketing: 'To send marketing communications (with your consent)',
    preventFraud: 'To prevent fraud and enhance security',
    legalObligations: 'To comply with legal obligations',
    dataSharing: '4. Data Sharing and Disclosure',
    dataSharingText: 'We do not sell your personal information. We may share your information with:',
    serviceProviders: 'Service providers who perform services on our behalf (shipping, payment processing)',
    legalAuthorities: 'Legal authorities when required by law',
    businessPartners: 'Business partners with your explicit consent',
    dataSecurity: '5. Data Security',
    dataSecurityText: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.',
    yourRights: '6. Your Rights',
    yourRightsText: 'You have the right to:',
    accessInfo: 'Access your personal information',
    correctInfo: 'Correct inaccurate information',
    deleteInfo: 'Request deletion of your information',
    objectProcessing: 'Object to processing of your information',
    dataPortability: 'Request data portability',
    withdrawConsent: 'Withdraw consent at any time',
    cookies: '7. Cookies',
    cookiesText: 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. You can control cookies through your browser settings, but disabling cookies may affect website functionality.',
    contactUsPrivacy: '8. Contact Us',
    contactUsPrivacyText: 'If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at support@ridersforge.com or call +421 912 123 456.',
    
    // Refund Policy Page
    refundPolicyTitle: 'Refund Policy',
    refundPolicyText: 'At Riders Forge, we want you to be completely satisfied with your purchase. If you are not satisfied, we offer a straightforward refund policy.',
    returnEligibility: 'Return Eligibility',
    returnEligibilityText: 'To be eligible for a return, your item must meet the following conditions:',
    returnCondition1: 'Items must be unworn, unwashed, and unused',
    returnCondition2: 'Items must be in their original packaging with all tags attached',
    returnCondition3: 'Items must be returned within 30 days of purchase',
    returnCondition4: 'Items must not be damaged or altered in any way',
    returnProcess: 'Return Process',
    returnProcessText: 'To return an item, please follow these steps:',
    returnStep1: 'Contact us at support@ridersforge.com to initiate a return',
    returnStep2: 'We will provide you with a return authorization and shipping instructions',
    returnStep3: 'Package the item securely in its original packaging',
    returnStep4: 'Ship the item back to us using a trackable shipping method',
    refundTimeline: 'Refund Timeline',
    refundTimelineText: 'Once we receive your returned item, we will inspect it and process your refund within 5-7 business days. The refund will be issued to your original payment method.',
    nonRefundableItems: 'Non-Refundable Items',
    nonRefundableItemsText: 'The following items are not eligible for return:',
    nonRefundable1: 'Items that have been worn, washed, or used',
    nonRefundable2: 'Items without original tags or packaging',
    nonRefundable3: 'Items returned after 30 days from purchase',
    refundContactText: 'If you have any questions about our refund policy, please contact us at support@ridersforge.com or call +421 912 123 456.',
    
    // Imprint Page
    companyInformation: 'Company Information',
    companyName: 'Company Name',
    responsibleForContent: 'Responsible for Content',
    responsibleForContentText: 'The content of this website is managed and maintained by Riders Forge. For any questions regarding the content, please contact us at support@ridersforge.com.',
    disclaimer: 'Disclaimer',
    disclaimerText: 'The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, Riders Forge excludes all representations, warranties, and conditions relating to this website and the use of this website.',
    liabilityForLinks: 'Liability for Links',
    liabilityForLinksText: 'Our website may contain links to external websites. We have no control over the nature, content, and availability of those sites. The inclusion of any links does not necessarily imply a recommendation or endorse the views expressed within them.',
    copyright: 'Copyright',
    copyrightText: 'All content on this website, including text, graphics, logos, and images, is the property of Riders Forge and is protected by copyright laws. Unauthorized use is prohibited.',
    
    // Payment Page
    acceptedPaymentMethods: 'Accepted Payment Methods',
    acceptedPaymentMethodsText: 'We accept the following payment methods for your convenience:',
    paymentMethod1: 'Credit Cards (Visa, Mastercard, American Express)',
    paymentMethod2: 'Debit Cards',
    paymentMethod3: 'PayPal',
    paymentMethod4: 'Bank Transfer',
    paymentSecurity: 'Payment Security',
    paymentSecurityText: 'All payment transactions are processed securely through encrypted connections. We do not store your full credit card information on our servers. Your payment details are handled by trusted third-party payment processors.',
    paymentProcessing: 'Payment Processing',
    paymentProcessingText: 'Payment is processed immediately upon order confirmation. For credit card payments, your card will be charged when you place your order. For bank transfers, please allow 2-3 business days for processing.',
    paymentContactText: 'If you have any questions about payment methods or encounter any payment issues, please contact us at support@ridersforge.com or call +421 912 123 456.',
    
    // Returns Page
    returnsExchanges: 'Returns & Exchanges',
    returnPolicy: 'Return Policy',
    returnPolicyText: 'We want you to love your purchase. If you\'re not completely satisfied, you can return most items within 30 days of purchase for a full refund or exchange.',
    howToReturn: 'How to Return',
    returnInstruction1: 'Log into your account and go to your order history',
    returnInstruction2: 'Select the item you wish to return and click "Return Item"',
    returnInstruction3: 'Print the return label and attach it to your package',
    returnInstruction4: 'Ship the item back to us using the provided return label',
    returnConditions: 'Return Conditions',
    returnsContactText: 'For questions about returns or exchanges, please contact us at support@ridersforge.com or call +421 912 123 456.',
    
    // Order Success Page
    orderPlacedSuccessfully: 'Order Placed Successfully!',
    thankYouOrder: 'Thank you for your order. We\'ve sent a confirmation email with your order details.',
    viewOrderHistory: 'View Order History',
    
    // Hero Component
    newCollection: 'New Collection',
    from: 'From',
    skullHoodie: 'Skull Hoodie',
    skullTee: 'Skull Tee',
    gloves: 'Gloves',
    backpack: 'Backpack',
    
    // Product Badges
    new: 'New',
    
    // Product Detail
    productDescriptionText: 'Premium quality {productName} from our {category} collection. Made with high-quality materials and attention to detail. Perfect for everyday wear or special occasions.',
    highQualityMaterials: 'High-quality materials',
    comfortableFit: 'Comfortable fit',
    durableConstruction: 'Durable construction',
    easyCare: 'Easy care',
  },
  sk: {
    // Header
    helpContact: 'Pomoc a Kontakt',
    login: 'Prihlásiť sa',
    logout: 'Odhlásiť sa',
    admin: 'Admin',
    cart: 'Nákupný košík',
    search: 'Vyhľadať',
    searchResults: 'Výsledky vyhľadávania',
    resultsFor: 'výsledkov pre',
    noResultsFor: 'Žiadne výsledky pre',
    enterSearchQuery: 'Zadajte hľadaný výraz vyššie pre nájdenie produktov.',
    
    // Navigation
    men: 'Muži',
    women: 'Ženy',
    mxGear: 'MX Vybavenie',
    accessories: 'Doplnky',
    graphics: 'Grafika',

    // Subcategories - Men
    hoodiesSweaters: 'Mikiny & Svetre',
    tshirts: 'Tričká',
    jackets: 'Bundy',
    pants: 'Nohavice',
    shorts: 'Šortky',
    underwear: 'Spodné prádlo',

    // Subcategories - Women
    sportsBrasBodysuits: 'Športové podprsenky & Body',
    womenHoodies: 'Mikiny',
    leggings: 'Legíny',
    sweatpants: 'Tepláky',

    // Subcategories - MX Gear
    mxGloves: 'Rukavice',
    mxGoggles: 'MX Okuliare',
    mxPants: 'MX Nohavice',
    mxJerseys: 'MX Dresy',
    helmets: 'Helmy',
    bags: 'Tašky',
    protectors: 'Chrániče',
    
    // Hero
    heroTitle: 'Prémiové Vybavenie pre Jazdcov',
    heroSubtitle: 'Objavte našu kolekciu kvalitného oblečenia a doplnkov',
    shopMen: 'Nakupovať Mužské',
    shopWomen: 'Nakupovať Ženské',
    
    // Common
    addToCart: 'Pridať do košíka',
    price: 'Cena',
    size: 'Veľkosť',
    quantity: 'Množstvo',
    pleaseSelectSize: 'Prosím vyberte veľkosť',
    total: 'Celkom',
    subtotal: 'Medzisúčet',
    shipping: 'Doprava',
    free: 'Zdarma',
    proceedToCheckout: 'Pokračovať k pokladni',
    continueShopping: 'Pokračovať v nákupe',
    remove: 'Odstrániť',
    emptyCart: 'Váš košík je prázdny',
    
    // Footer
    about: 'O nás',
    contact: 'Kontakt',
    faq: 'FAQ',
    terms: 'Podmienky',
    privacy: 'Súkromie',
    newsletter: 'Newsletter',
    refundPolicy: 'Politika vrátenia',
    imprint: 'Impressum',
    payment: 'Platba',
    returns: 'Vrátenia',
    phone: 'Telefón',
    email: 'Email',
    businessHours: 'Úradné hodiny',
    newsletterText: 'Prihláste sa na odber nášho newsletteru a získajte najnovšie aktualizácie, exkluzívne ponuky a oznámenia o nových produktoch.',
    newsletterPageSubtitle: 'Zostaňte informovaní o našich najnovších produktoch, exkluzívnych ponukách a novinkách.',
    whySubscribe: 'Prečo sa prihlásiť?',
    newsletterBenefit1: 'Získajte exkluzívne zľavy a skorý prístup k výpredajom',
    newsletterBenefit2: 'Buďte prví, ktorí sa dozvedia o nových produktoch',
    newsletterBenefit3: 'Dostávajte tipy na štýl a aktualizácie komunity jazdcov',
    newsletterBenefit4: 'Špeciálne ponuky dostupné len pre odberateľov',
    subscribeNow: 'Prihlásiť sa teraz',
    newsletterPrivacy: 'Rešpektujeme vaše súkromie. Môžete sa kedykoľvek odhlásiť. Nikdy nebudeme zdieľať vašu emailovú adresu s tretími stranami.',
    emailPlaceholder: 'Zadajte svoju emailovú adresu',
    subscribe: 'Prihlásiť sa',
    newsletterSuccess: 'Ďakujeme za prihlásenie sa na odber nášho newsletteru!',
    learnMoreNewsletter: 'Dozvedieť sa viac o našom newsletteri',
    
    // Graphics Page
    graphicsTitle: 'Vlastný Dizajn Grafiky Na Váš Motocykel',
    graphicsSubtitle: 'Premenťe svoj motocykel pomocou grafiky na mieru navrhnutej práve pre vás',
    readyToStart: 'Ste pripravení začať?',
    requestQuote: 'Požiadať o cenovú ponuku',
    closeForm: 'Zatvoriť formulár',
    contactUsToday: 'Kontaktujte nás a navrhneme grafiku na váš motocykel už dnes!',
    graphicsDescription: 'V Riders Forge sa špecializujeme na vytváranie vlastnej motocyklovej grafiky, ktorá odráža váš jedinečný štýl a osobnosť. Či už hľadáte vlastné logá, kompletnú fóliu alebo personalizované návrhy, náš dizajnérsky tím môže zrealizovať vašu víziu.',
    customDesign: 'Vlastný Dizajn',
    customDesignDesc: 'Spolupracujte s našimi dizajnermi na vytvorení grafiky, ktorá zodpovedá vášmu štýlu a preferenciám',
    bikeSpecific: 'Špecifické pre Motocykel',
    bikeSpecificDesc: 'Dizajny prispôsobené vášmu konkrétnemu modelu a roku motocykla',
    preciseFit: 'Presné Prispôsobenie',
    preciseFitDesc: 'Zaručené perfektné prispôsobenie s našimi presnými meraniami a šablónami',
    premiumQuality: 'Prémiová Kvalita',
    premiumQualityDesc: 'Vysokokvalitné vinylové materiály, ktoré sú odolné a odolné voči poveternostným vplyvom',
    ourDesignProcess: 'Náš Proces Dizajnu',
    consultation: 'Konzultácia',
    consultationDesc: 'Kontaktujte nás so svojimi nápadmi, modelom motocykla a preferenciami dizajnu',
    design: 'Dizajn',
    designDesc: 'Náš tím vytvorí vlastné návrhy na základe vašich požiadaviek',
    reviewRevise: 'Kontrola a Úpravy',
    reviewReviseDesc: 'Skontrolujte dizajny a požiadajte o zmeny, kým to nebude perfektné',
    production: 'Výroba',
    productionDesc: 'Po schválení vyrobíme vašu grafiku s dokonalou a presnosťou',
    installation: 'Inštalácia',
    installationDesc: 'Svoju grafiku si môžete vyzdvihnúť u nás na predajni a môžeme Vám pomôcť s inštaláciou',
    graphicsFormTitle: 'Formulár na Požiadanie o Dizajn Grafiky',
    fullName: 'Celé Meno',
    emailLabel: 'Email',
    phoneNumber: 'Telefónne Číslo',
    bikeModel: 'Model Motocykla',
    bikeYear: 'Rok Motocykla',
    designType: 'Typ Dizajnu',
    selectDesignType: 'Vyberte typ dizajnu',
    fullBodyWrap: 'Kompletná Fólia',
    racingStripes: 'Pretekárske Pruhy',
    customLogos: 'Vlastné Logá',
    numberPlates: 'Číselné Tabuľky',
    decalsStickers: 'Nálepky a Samolepky',
    other: 'Iné',
    designDescription: 'Popis Dizajnu',
    designDescriptionPlaceholder: 'Opíšte svoje nápady na dizajn, farby, preferencie štýlu atď...',
    budgetRange: 'Rozpočet',
    selectBudget: 'Vyberte rozpočet',
    under200: 'Pod €200',
    budget200500: '€200 - €500',
    budget5001000: '€500 - €1,000',
    over1000: 'Nad €1,000',
    timeline: 'Časový Harmonogram',
    selectTimeline: 'Vyberte časový harmonogram',
    asap: 'Čo najskôr',
    weeks12: '1-2 týždne',
    month1: '1 mesiac',
    flexible: 'Flexibilné',
    submitRequest: 'Odoslať Žiadosť',
    thankYouMessage: 'Ďakujeme! Čoskoro vás budeme kontaktovať, aby sme prediskutovali váš projekt motocyklovej grafiky.',
    namePlaceholder: 'Ján Novák',
    phonePlaceholder: '+421 912 123 456',
    bikeModelPlaceholder: 'napr., Yamaha YZ450F',
    bikeYearPlaceholder: 'napr., 2023',
    
    // Cart Page
    shoppingCart: 'Nákupný košík',
    yourCartIsEmpty: 'Váš košík je prázdny',
    returnToCart: 'Späť do košíka',
    pleaseFillAllFields: 'Prosím vyplňte všetky povinné polia.',
    startShopping: 'Začnite nakupovať a pridajte položky do košíka',
    orderSummary: 'Súhrn objednávky',
    itemSubtotal: 'Medzisúčet položky',
    freeShippingAt: 'Pridajte ešte {amount} pre bezplatnú dopravu',
    freeShippingNote: 'Máte nárok na bezplatnú dopravu!',
    maximumInStock: 'Maximum na sklade',
    
    // Checkout Page
    checkout: 'Pokladňa',
    contactInformation: 'Kontaktné informácie',
    shippingAddress: 'Dodacia adresa',
    paymentMethod: 'Spôsob platby',
    paymentMethodUpdated: 'Spôsob platby bol úspešne aktualizovaný!',
    defaultPaymentMethod: 'Predvolený spôsob platby',
    changePaymentMethod: 'Zmeniť spôsob platby',
    creditDebitCard: 'Kreditná/Debetná karta',
    paypal: 'PayPal',
    bankTransfer: 'Bankový prevod',
    firstName: 'Meno',
    lastName: 'Priezvisko',
    address: 'Adresa',
    city: 'Mesto',
    postalCode: 'PSČ',
    country: 'Krajina',
    placeOrder: 'Objednať',
    placeOrderError: 'Objednávku sa nepodarilo odoslať. Skúste to znova.',
    placingOrder: 'Vytváram objednávku...',
    processing: 'Spracováva sa...',
    
    // Product Detail
    description: 'Popis',
    shippingReturns: 'Doprava a vrátenie',
    shippingReturnsText: 'Bezplatná doprava pri objednávkach nad €50. Jednoduché vrátenie do 30 dní.',
    selectSize: 'Prosím vyberte veľkosť',
    addedToCart: 'Pridané do košíka!',
    
    // Category Page
    productsFound: 'Nájdených {count} produkt{plural}',
    noProductsFound: 'V tejto kategórii sa nenašli žiadne produkty.',
    
    // Common buttons
    save: 'Ušetríte',
    cancel: 'Zrušiť',
    back: 'Späť',
    next: 'Ďalej',
    submit: 'Odoslať',
    close: 'Zatvoriť',
    outOfStock: 'Vypredané',
    allInCart: 'Všetko v košíku',
    availableToAdd: 'dostupných na pridanie',
    alreadyInCart: 'už v košíku',
    inSize: 'veľkosť',
    thisSizeOutOfStock: 'Táto veľkosť je vypredaná',
    noMoreInStockForSize: 'Na túto veľkosť už nie je skladom.',
    onlyXMoreAvailable: 'Na túto veľkosť môžete pridať ešte {available} ks ({stock} na sklade, {inCart} v košíku).',
    notSpecified: 'Neurčené',
    backToAccount: 'Späť na účet',
    loadingOrderDetails: 'Načítavanie detailov objednávky...',
    orderNotFound: 'Objednávka sa nenašla',
    noPermissionViewOrder: 'Nemáte oprávnenie zobraziť túto objednávku.',
    failedToLoadOrder: 'Nepodarilo sa načítať detaily objednávky.',
    
    // Account
    myAccount: 'Môj účet',
    profile: 'Profil',
    orderHistory: 'História objednávok',
    addresses: 'Adresy',
    paymentMethods: 'Spôsoby platby',
    
    // Login
    createAccount: 'Vytvoriť účet',
    welcomeBack: 'Vitajte späť! Prosím prihláste sa do svojho účtu.',
    createAccountSubtitle: 'Vytvorte si účet na sledovanie objednávok a uloženie vašich preferencií.',
    rememberMe: 'Zapamätať si ma',
    forgotPassword: 'Zabudli ste heslo?',
    register: 'Registrovať sa',
    signUp: 'Registrovať sa',
    noAccount: 'Nemáte účet? ',
    haveAccount: 'Už máte účet? ',
    firstNamePlaceholder: 'Ján',
    lastNamePlaceholder: 'Novák',
    password: 'Heslo',
    pleaseProvideName: 'Prosím zadajte meno a priezvisko',
    emailAlreadySignedIn: 'Tento email už bol zaregistrovaný. Prosím použite iný email alebo sa prihláste.',
    invalidCredentials: 'Neplatný email alebo heslo. Prosím skontrolujte svoje údaje a skúste znova.',
    errorOccurred: 'Nastala chyba. Prosím skúste znova.',
    checkYourEmail: 'Skontrolujte svoj e-mail',
    resetEmailSent: 'Ak účet s touto e-mailovou adresou existuje, poslali sme inštrukcie na obnovenie hesla.',
    recoveryCodeBelow: 'Váš kód na obnovenie hesla je v simulovanej notifikácii nižšie.',
    passwordResetEmail: 'Obnovenie hesla',
    yourRecoveryCode: 'Váš kód na obnovenie hesla',
    recoveryCodeMessage: 'Použite tento kód na obnovenie hesla:',
    recoveryCodeHint: 'Zadajte tento kód na ďalšej obrazovke spolu s novým heslom.',
    continueToResetPassword: 'Pokračovať na obnovenie hesla',
    noMessageForEmail: 'Pre túto e-mailovú adresu nebola nájdená žiadna žiadosť o obnovenie hesla.',
    forgotPasswordSubtitle: 'Zadajte svoj e-mail a zobrazíme vám kód na obnovenie v simulovanej e-mailovej notifikácii.',
    sendRecoveryCode: 'Odoslať kód na obnovenie',
    backToLogin: 'Späť na prihlásenie',
    resetPassword: 'Obnoviť heslo',
    resetPasswordSubtitle: 'Zadajte kód na obnovenie a nové heslo nižšie.',
    recoveryCode: 'Kód na obnovenie',
    recoveryCodePlaceholder: 'Vložte kód z e-mailu',
    newPassword: 'Nové heslo',
    confirmPassword: 'Potvrdiť heslo',
    passwordsDoNotMatch: 'Heslá sa nezhodujú.',
    passwordMinLength: 'Heslo musí mať aspoň 6 znakov.',
    recoveryCodeRequired: 'Prosím zadajte kód na obnovenie z e-mailu.',
    requestNewCode: 'Požiadať o nový kód',
    passwordUpdated: 'Heslo bolo zmenené. Teraz sa môžete prihlásiť.',
    invalidResetLink: 'Neplatný odkaz. Použite odkaz z e-mailu.',
    invalidResetLinkSubtitle: 'Tento odkaz chýba alebo je neplatný. Požiadajte o nové obnovenie hesla na stránke prihlásenia.',
    requestNewLink: 'Požiadať o nový odkaz',
    inStock: 'na sklade',
    quantityPerSize: 'Množstvo podľa veľkosti',
    quantityPerSizeHint: 'Nastavte množstvo pre každú veľkosť. Celkové množstvo sa vypočíta automaticky.',
    
    // Footer
    followUs: 'Sledujte nás',
    customerService: 'Zákaznícky servis',
    contactRequests: 'Kontaktné požiadavky',
    graphicsRequests: 'Požiadavky na grafiku',
    noContactRequests: 'Nenašli sa žiadne kontaktné požiadavky.',
    noGraphicsRequests: 'Nenašli sa žiadne požiadavky na grafiku.',
    inProgress: 'Prebieha',
    resolved: 'Vyriešené',
    sending: 'Odosielam...',
    submitting: 'Odosielam...',
    messageSentError: 'Zlyhalo odoslanie správy. Skúste to znova.',
    requestError: 'Zlyhalo odoslanie požiadavky. Skúste to znova.',
    contactRequestDetails: 'Detaily kontaktnej požiadavky',
    graphicsRequestDetails: 'Detaily požiadavky na grafiku',
    messageDetails: 'Detaily správy',
    bikeInformation: 'Informácie o motocykli',
    projectDetails: 'Detaily projektu',
    information: 'Informácie',
    allRightsReserved: 'Všetky práva vyhradené',
    imageCredit: 'Obrázky produktov',
    imageCreditFrom: 'z',
    
    // Product Detail
    productNotFound: 'Produkt sa nenašiel',
    goToHome: 'Prejsť na domovskú stránku',
    saveAmount: 'Ušetriť €{amount}',
    
    // Account Page
    pleaseLogin: 'Prosím prihláste sa na zobrazenie vášho účtu.',
    profileUpdated: 'Profil bol úspešne aktualizovaný!',
    updateFailed: 'Aktualizácia profilu zlyhala. Skúste to znova.',
    profileInformation: 'Informácie o profile',
    saveChanges: 'Uložiť zmeny',
    noOrdersYet: 'Zatiaľ žiadne objednávky.',
    order: 'Objednávka',
    viewDetails: 'Zobraziť detaily',
    savedAddresses: 'Uložené adresy',
    defaultAddress: 'Predvolená adresa',
    edit: 'Upraviť',
    addNewAddress: 'Pridať novú adresu',
    noSavedPaymentMethods: 'Žiadne uložené spôsoby platby.',
    addPaymentMethod: 'Pridať spôsob platby',
    
    // Home Page
    highlights: 'Najlepšie produkty',
    allProducts: 'Všetky produkty',
    loadingProducts: 'Načítavanie produktov...',
    
    // Admin Dashboard
    adminDashboard: 'Admin Dashboard',
    adminWelcomeBack: 'Vitajte späť, {name}',
    overview: 'Prehľad',
    products: 'Produkty',
    orders: 'Objednávky',
    customers: 'Zákazníci',
    settings: 'Nastavenia',
    loadingDashboardData: 'Načítavanie údajov dashboardu...',
    failedToLoadDashboard: 'Nepodarilo sa načítať údaje dashboardu. Prosím skontrolujte, či beží backend.',
    totalOrders: 'Celkový počet objednávok',
    totalRevenue: 'Celkový príjem',
    totalProducts: 'Celkový počet produktov',
    totalCustomers: 'Celkový počet zákazníkov',
    pendingOrders: 'Čakajúce objednávky',
    completedOrders: 'Dokončené objednávky',
    location: 'Poloha',
    totalSpent: 'Celková suma',
    registeredDate: 'Registrovaný',
    active: 'Aktívny',
    inactive: 'Neaktívny',
    noCustomers: 'Nenašli sa žiadni zákazníci.',
    orderDetails: 'Detaily objednávky',
    orderStatus: 'Stav objednávky',
    orderDate: 'Dátum objednávky',
    totalAmount: 'Celková suma',
    noItems: 'Nenašli sa žiadne položky',
    noItemsInOrder: 'V tejto objednávke nie sú žiadne položky.',
    customerInformation: 'Informácie o zákazníkovi',
    orderInformation: 'Informácie o objednávke',
    orderItems: 'Položky objednávky',
    updateStatusError: 'Zlyhalo aktualizovanie stavu objednávky. Skúste to znova.',
    pending: 'Čakajúce',
    shipped: 'Odoslané',
    delivered: 'Doručené',
    cancelled: 'Zrušené',
    items: 'Položky',
    product: 'Produkt',
    productId: 'ID produktu',
    unitPrice: 'Jednotková cena',
    itemsTotal: 'Celkom položiek',
    orderTotal: 'Celková suma objednávky',
    unknownProduct: 'Neznámy produkt',
    addressUpdated: 'Adresa bola úspešne aktualizovaná!',
    noAddressSaved: 'Zatiaľ nie je uložená žiadna adresa. Kliknite na Upraviť pre pridanie.',
    enterAddress: 'Zadajte svoju adresu',
    enterCity: 'Zadajte svoje mesto',
    enterPostalCode: 'Zadajte PSČ',
    saving: 'Ukladám...',
    recentOrders: 'Nedávne objednávky',
    orderId: 'ID objednávky',
    customer: 'Zákazník',
    amount: 'Suma',
    status: 'Stav',
    date: 'Dátum',
    actions: 'Akcie',
    view: 'Zobraziť',
    noRecentOrders: 'Nenašli sa žiadne nedávne objednávky.',
    noOrders: 'Nenašli sa žiadne objednávky.',
    productsManagement: 'Správa produktov',
    addNewProduct: '+ Pridať nový produkt',
    subcategory: 'Podkategória',
    productsManagementComingSoon: 'Rozhranie pre správu produktov čoskoro...',
    productName: 'Názov produktu',
    productNamePlaceholder: 'Zadajte názov produktu',
    regularPrice: 'Pravidelná cena',
    optional: 'Voliteľné',
    sizesPlaceholder: 'S, M, L, XL, XXL',
    sizesHint: 'Zadajte veľkosti oddelené čiarkami (napr., S, M, L, XL)',
    imageUrl: 'URL obrázka',
    imageUrlPlaceholder: 'https://example.com/image.jpg',
    imageUrlHint: 'Ponechajte prázdne pre použitie zástupného obrázka',
    markAsNew: 'Označiť ako nový produkt',
    createProduct: 'Vytvoriť produkt',
    creating: 'Vytváranie...',
    productCreatedSuccess: 'Produkt bol úspešne vytvorený!',
    editProduct: 'Upraviť produkt',
    updateProduct: 'Aktualizovať produkt',
    updating: 'Aktualizovanie...',
    productUpdatedSuccess: 'Produkt bol úspešne aktualizovaný!',
    delete: 'Zmazať',
    confirmDeleteProduct: 'Naozaj chcete zmazať tento produkt?',
    deleteProductError: 'Zlyhalo zmazanie produktu. Skúste to znova.',
    noProducts: 'Nenašli sa žiadne produkty.',
    image: 'Obrázok',
    loading: 'Načítavanie...',
    clickToZoom: 'Kliknite pre zväčšenie',
    ordersManagement: 'Správa objednávok',
    ordersManagementComingSoon: 'Rozhranie pre správu objednávok čoskoro...',
    customersManagement: 'Správa zákazníkov',
    customersManagementComingSoon: 'Rozhranie pre správu zákazníkov čoskoro...',
    settingsComingSoon: 'Rozhranie nastavení čoskoro...',
    
    // Contact Page
    contactUs: 'Kontaktujte nás',
    contactSubtitle: 'Máte otázku alebo potrebujete pomoc? Sme tu pre vás!',
    getInTouch: 'Spojte sa s nami',
    contactSupportText: 'Náš tím zákazníckeho servisu je k dispozícii, aby vám pomohol s akýmikoľvek otázkami alebo obavami.',
    phoneWhatsapp: 'Telefón a WhatsApp',
    contactHours: 'Pondelok až piatok, 09:00 až 16:00',
    respondWithin24: 'Odpovedáme do 24 hodín',
    support: 'Podpora',
    shippingDelivery: 'Doprava a dodanie',
    shippingPageSubtitle: 'Všetko, čo potrebujete vedieť o dodaní.',
    shippingOptions: 'Možnosti dopravy',
    shippingOptionsText: 'Ponúkame štandardnú a expresnú dopravu. Pri pokladni si vyberte možnosť, ktorá vám najviac vyhovuje.',
    standardShipping: 'Štandardná doprava',
    standardShippingDesc: '5–7 pracovných dní v rámci Európy, 7–14 dní medzinárodne. €5,99 alebo zdarma pri objednávkach nad €50.',
    expressShipping: 'Expresná doprava',
    expressShippingDesc: '2–3 pracovné dni. K dispozícii pri pokladni za príplatok.',
    freeShipping: 'Bezplatná doprava',
    freeShippingText: 'Bezplatná štandardná doprava pri všetkých objednávkach nad €50. Žiadny kód nie je potrebný – vaša objednávka sa automaticky kvalifikuje pri pokladni.',
    orderProcessing: 'Spracovanie objednávky',
    orderProcessingText: 'Objednávky sa zvyčajne spracujú do 1–2 pracovných dní. Po odoslaní objednávky dostanete e-mailové potvrdenie so sledovacími informáciami.',
    internationalShipping: 'Medzinárodná doprava',
    internationalShippingText: 'Dodávame do viac ako 80 krajín na celom svete. Časy dodania a colné formalities sa líšia podľa vašej polohy. Za akékoľvek dovozné clá alebo dane vo vašej krajine ste zodpovední vy.',
    trackingYourOrder: 'Sledovanie objednávky',
    trackingText: 'Po odoslaní objednávky dostanete e-mailom odkaz na sledovanie. Stav objednávky môžete tiež zobraziť vo svojom účte v sekcii História objednávok.',
    shippingContactText: 'Máte otázky k doprave? Kontaktujte nás na support@grenzgaenger-shop.com alebo navštívte stránku Kontakt.',
    sendUsMessage: 'Pošlite nám správu',
    name: 'Meno',
    subject: 'Predmet',
    message: 'Správa',
    sendMessage: 'Odoslať správu',
    messageSentSuccess: 'Správa bola úspešne odoslaná!',
    
    // FAQ Page
    frequentlyAskedQuestions: 'Často kladené otázky',
    faqSubtitle: 'Nájdite odpovede na bežné otázky o našich produktoch, doprave, vráteniach a ďalšie.',
    questionsAboutStore: 'Otázky o obchode',
    stillHaveQuestions: 'Stále máte otázky?',
    cantFindAnswer: 'Nemôžete nájsť odpoveď, ktorú hľadáte? Neváhajte nás kontaktovať.',
    contactUsBtn: 'Kontaktujte nás',
    faq1Question: 'Kam Riders Forge dodáva?',
    faq1Answer: 'Riders Forge dodáva po celom svete! Dodávame do takmer každej krajiny, spájame jazdcov globálne s prémiovým vybavením a oblečením. Či už ste v Európe, Amerike, Ázii alebo kdekoľvek inde, máme vás pokrytých. Štandardná doprava zvyčajne trvá 5-7 pracovných dní v rámci Európy a 7-14 pracovných dní medzinárodne. Možnosti expresnej dopravy sú k dispozícii pri pokladni pre rýchlejšie dodanie.',
    faq2Question: 'Ako vás môžem kontaktovať, ak mám požiadavku alebo otázku?',
    faq2Answer: 'Pre dotazy e-mailom môžete kontaktovať náš tím zákazníckeho servisu na support@ridersforge.com. Môžete nás tiež kontaktovať od pondelka do piatka (okrem sviatkov) od 09:00 do 16:00 telefonicky alebo cez WhatsApp na +421 912 123 456. Náš AI chatbot je tiež k dispozícii 24/7 pre okamžité odpovede na bežné otázky - stačí kliknúť na tlačidlo chatu v pravom dolnom rohu!',
    faq3Question: 'Máte fyzický obchod?',
    faq3Answer: 'Sme online obchod špecializujúci sa na prémiové vybavenie pre jazdcov. Hoci momentálne nemáme fyzickú pobočku, občas organizujeme pop-up akcie, kde si môžete naše produkty pozrieť a vyskúšať osobne. Sledujte nadchádzajúce akcie prostredníctvom nášho newsletteru a sociálnych médií.',
    faq4Question: 'Aká je vaša politika vrátenia?',
    faq4Answer: 'Ponúkame 30-dňovú politiku vrátenia. Položky musia byť nenosenej, neumyté a v pôvodnom balení so štítkami. Môžete iniciovať vrátenie prostredníctvom vášho účtu alebo nás kontaktovať na support@ridersforge.com. Po prijatí vášho vrátenia spracujeme váš refund do 5-7 pracovných dní.',
    faq5Question: 'Ako dlho trvá doprava?',
    faq5Answer: 'Štandardná doprava zvyčajne trvá 3-7 pracovných dní v rámci Európy a 7-14 pracovných dní pre medzinárodné objednávky. Možnosti expresnej dopravy sú k dispozícii pri pokladni pre rýchlejšie dodanie. Bezplatná doprava je k dispozícii pri objednávkach nad €50.',
    faq6Question: 'Aké spôsoby platby akceptujete?',
    faq6Answer: 'Akceptujeme všetky hlavné kreditné karty (Visa, Mastercard, American Express), PayPal a bankové prevody. Všetky platby sú spracované bezpečne prostredníctvom nášho šifrovaného platobného brány.',
    faq7Question: 'Môžem sledovať svoju objednávku?',
    faq7Answer: 'Áno! Po odoslaní vašej objednávky dostanete sledovacie číslo e-mailom. Toto číslo môžete použiť na sledovanie vašej zásielky na našej webovej stránke alebo na webovej stránke dopravcu.',
    faq8Question: 'Ponúkate darčekové poukazy?',
    faq8Answer: 'V súčasnosti neponúkame fyzické ani digitálne darčekové poukazy. Môžete však zakúpiť položky ako darčeky a môžeme pridať personalizovanú správu k vašej objednávke. Prosím pridajte detaily darčekovej správy pri pokladni.',
    
    // Chatbot
    chatbotWelcome: 'Dobrý deň! Som váš asistent AI podpory. Ako vám môžem dnes pomôcť?',
    chatbotTitle: 'Asistent AI podpory',
    chatbotOnline: 'Online',
    chatbotPlaceholder: 'Napíšte svoju správu...',
    chatbotCloseLabel: 'Zavrieť chat',
    chatbotGreetingReply: 'Dobrý deň! Som tu, aby som vám pomohol s akýmikoľvek otázkami o Riders Forge. Čo by ste chceli vedieť?',
    chatbotShipping: 'Ponúkame štandardnú dopravu (5-7 pracovných dní) a expresnú dopravu (2-3 pracovné dni). Bezplatná doprava pri objednávkach nad €50. Objednávky sa zvyčajne spracujú do 1-2 pracovných dní.',
    chatbotReturns: 'Ponúkame 30-dňovú politiku vrátenia. Tovar musí byť nosený, nepraný a v pôvodnom balení. Vrátenie môžete iniciovať cez svoj účet alebo kontaktovať support@ridersforge.com.',
    chatbotPayment: 'Akceptujeme kreditné/debetné karty, PayPal a bankové prevody. Všetky platby sú spracované bezpečne. Vaše platobné údaje sú šifrované a nikdy sa neukladajú na našich serveroch.',
    chatbotProducts: 'Produkty môžete prehliadať podľa kategórií (Muži, Ženy, MX výstroj, Doplnky). Na každej stránke produktu sú zobrazené dostupné veľkosti a stav skladu. Ak potrebujete pomôcť nájsť konkrétny produkt, napíšte mi!',
    chatbotOrderTracking: 'Stav objednávky môžete sledovať v nástenke účtu. Po odoslaní objednávky dostanete e-mailom sledovacie číslo. Pre pomoc kontaktujte support@ridersforge.com.',
    chatbotSizing: 'Tabuľky veľkostí sú na každej stránke produktu. Ak si nie ste istí veľkosťou, odporúčame pozrieť si sprievodcu veľkosťami alebo kontaktovať náš tím podpory.',
    chatbotContact: 'Môžete nás kontaktovať:\n• Telefón: +421 912 123 456\n• E-mail: support@ridersforge.com\n• Hodiny: Po-Pia 09:00 - 16:00\nViac možností nájdete na stránke Kontakt.',
    chatbotNavigate: 'Produkty môžete prehliadať v hlavnej ponuke podľa kategórií: Muži, Ženy, MX výstroj a Doplnky, alebo si pozrite odporúčané produkty na domovskej stránke.',
    chatbotAccount: 'Účet vytvoríte kliknutím na „Prihlásenie“ v pravom hornom rohu a výberom „Registrovať sa“. S účtom môžete sledovať objednávky, ukladať adresy a spravovať preferencie.',
    chatbotHelp: 'Som tu, aby som pomohol! Môžete sa ma opýtať na:\n• Dopravu a doručenie\n• Vrátenia a refundácie\n• Informácie o produktoch\n• Sledovanie objednávok\n• Spôsoby platby\n• Sprievodcov veľkosťami\n• Alebo čokoľvek o Riders Forge!',
    chatbotCommandsList: 'Tu sú témy, na ktoré viem odpovedať (môžete písať po slovensky alebo po anglicky):\n\n• Doprava – shipping, delivery, doprava, doručenie\n• Vrátenia – return, refund, vrátenie, reklamácia\n• Platba – payment, pay, platba, karta\n• Produkty – product, stock, produkt, sklad\n• Sledovanie objednávky – order, track, objednávka, sledovanie\n• Veľkosti – size, sizing, veľkosť, meranie\n• Kontakt – contact, support, kontakt, telefón\n• Navigácia – menu, categories, ponuka, kategórie\n• Účet – account, login, účet, registrácia\n• Pomoc – help, ?, príkazy, pomoc\n\nStačí napísať slovo z ktorejkoľvek témy a odpoviem!',
    chatbotDefaultReply: 'Rozumiem, pýtate sa na: „{message}". Môžete uviesť viac detailov? Náš tím podpory na support@ridersforge.com vám môže okamžite pomôcť.',
    chatbotButtonTitle: 'Potrebujete pomoc? Napíšte nám!',
    
    // About Page
    aboutRidersForge: 'O Riders Forge',
    premiumGearForRiders: 'Prémiové Vybavenie pre Jazdcov',
    ourStory: 'Náš Príbeh',
    ourStoryText1: 'Riders Forge vzniklo z vášne pre motocyklizmus a túžby vytvoriť kvalitné vybavenie, ktoré kombinuje štýl, funkčnosť a odolnosť. Založené s poslaním poskytovať prémiové vybavenie pre jazdcov, slúžime globálnej motocyklovej komunite s oddanosťou a excelentnosťou.',
    ourStoryText2: 'To, čo začalo ako malá operácia, sa rozvinulo do medzinárodnej značky, ktorá dodáva produkty po celom svete. Naše záväzok k kvalite a spokojnosti zákazníkov nás urobil dôveryhodným menom v motocyklovom oblečení a doplnkoch.',
    ourMission: 'Naša Misia',
    ourMissionText1: 'V Riders Forge veríme, že každý jazdec si zaslúži vybavenie, ktoré nielenže vyzerá skvele, ale tiež funguje na najvyššej úrovni. Navrhujeme a vyberáme produkty, ktoré spĺňajú požiadavky moderných jazdcov, pričom zachovávajú nadčasový štýl a vynikajúcu kvalitu.',
    ourMissionText2: 'Našou misiou je poskytovať jazdcom prémiové vybavenie, ktoré zlepšuje ich jazdecký zážitok, či už sú na trati, chodníku alebo ulici. Sme oddaní kvalite, inováciám a jazdeckej komunite.',
    whatWeOffer: 'Čo Ponúkame',
    globalShipping: 'Globálna Doprava',
    globalShippingDesc: 'Dodávame po celom svete do viac ako 80 krajín, prinášame naše produkty priamo jazdcom všade.',
    communityDriven: 'Komunitne Riadené',
    communityDrivenDesc: 'Naša komunita jazdcov je v srdci všetkého, čo robíme. Vaša spätná väzba formuje naše produkty.',
    customerFirst: 'Zákazník na Prvom Mieste',
    customerFirstDesc: 'Vynikajúci zákaznícky servis je naša priorita. Sme tu, aby sme vám pomohli nájsť perfektné vybavenie.',
    joinOurCommunity: 'Pripojte sa k našej komunite',
    joinCommunityText: 'Sledujte nás na sociálnych médiách, aby ste zostali v kontakte s komunitou Riders Forge, získali najnovšie aktualizácie o nových produktoch a zdieľali svoje jazdecké dobrodružstvá so spolujazdcami po celom svete.',
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
    youtube: 'YouTube',
    
    // Terms Page
    termsOfService: 'Podmienky služby',
    lastUpdated: 'Naposledy aktualizované: január 2024',
    acceptanceOfTerms: '1. Prijatie podmienok',
    acceptanceText: 'Prístupom a použitím tejto webovej stránky akceptujete a súhlasíte s tým, že budete viazaní podmienkami a ustanoveniami tejto dohody. Ak nesúhlasíte s dodržiavaním vyššie uvedeného, nepoužívajte túto službu.',
    useLicense: '2. Licencia na použitie',
    useLicenseText: 'Povolenie je udelené na dočasné stiahnutie jednej kópie materiálov na webovej stránke Riders Forge len na osobnú, nekomerčnú dočasnú prehliadku. Toto je udelenie licencie, nie prevod vlastníctva, a pod touto licenciou nesmiete:',
    modifyCopy: 'upravovať alebo kopírovať materiály',
    commercialUse: 'používať materiály na akýkoľvek komerčný účel alebo na akúkoľvek verejnú prezentáciu',
    decompile: 'pokúšať sa dekompilovať alebo spätne inžinierovať akýkoľvek softvér obsiahnutý na webovej stránke',
    removeCopyright: 'odstrániť akékoľvek autorské práva alebo iné vlastnícke označenia z materiálov',
    productsPricing: '3. Produkty a ceny',
    productsPricingText: 'Vyhradzujeme si právo kedykoľvek upraviť ceny produktov bez predchádzajúceho upozornenia. Všetky ceny sú v EUR, pokiaľ nie je uvedené inak. Obrázky produktov slúžia len na ilustračné účely a môžu sa líšiť od skutočného produktu. Snažíme sa zabezpečiť, aby informácie o produktoch boli presné, ale nezaručujeme, že popisy produktov alebo iný obsah je bezchybný.',
    ordersPayment: '4. Objednávky a platba',
    ordersPaymentText: 'Keď zadáte objednávku, robíte ponuku na nákup produktov. Vyhradzujeme si právo prijať alebo odmietnuť vašu objednávku. Platba musí byť prijatá pred spracovaním vašej objednávky. Akceptujeme hlavné kreditné karty, PayPal a bankové prevody.',
    shippingDeliveryTerms: '5. Doprava a dodanie',
    shippingDeliveryText: 'Dodávame po celom svete do viac ako 80 krajín. Časy dodania sa líšia podľa polohy. Nie sme zodpovední za oneskorenia spôsobené colnicami, počasím alebo inými okolnosťami mimo našej kontroly. Riziko straty a vlastníctvo produktov prechádza na vás pri dodaní dopravcovi.',
    returnsRefunds: '6. Vrátenia a refundácie',
    returnsRefundsText: 'Môžete vrátiť položky do 30 dní od nákupu za úplný refund, za predpokladu, že sú v pôvodnom stave so štítkami. Náklady na dopravu pri vrátení sú zodpovednosťou zákazníka, pokiaľ položka nie je chybná alebo sme urobili chybu.',
    limitationLiability: '7. Obmedzenie zodpovednosti',
    limitationLiabilityText: 'Riders Forge nebude zodpovedné za akékoľvek nepriame, náhodné, špeciálne, následné alebo trestné škody vyplývajúce z vášho použitia alebo neschopnosti použiť službu.',
    privacyPolicyTerms: '8. Zásady ochrany súkromia',
    privacyPolicyTermsText: 'Vaše použitie tejto webovej stránky je tiež riadené našimi Zásadami ochrany súkromia. Prosím, prečítajte si naše Zásady ochrany súkromia, aby ste pochopili naše postupy.',
    contactInformationText: 'Ak máte akékoľvek otázky týkajúce sa týchto Podmienok služby, kontaktujte nás na support@ridersforge.com alebo zavolajte na +421 912 123 456.',
    
    // Privacy Page
    privacyPolicy: 'Zásady ochrany súkromia',
    introduction: '1. Úvod',
    introductionText: 'V Riders Forge sme oddaní ochrane vášho súkromia. Tieto Zásady ochrany súkromia vysvetľujú, ako zhromažďujeme, používame, zverejňujeme a chránime vaše informácie pri návšteve našej webovej stránky a použití našich služieb.',
    informationWeCollect: '2. Informácie, ktoré zhromažďujeme',
    personalInformation: '2.1 Osobné informácie',
    personalInfoText: 'Zhromažďujeme osobné informácie, ktoré nám poskytujete, vrátane:',
    nameContact: 'Meno a kontaktné informácie (e-mailová adresa, telefónne číslo, poštová adresa)',
    paymentInfo: 'Platobné informácie (spracované bezpečne prostredníctvom našich poskytovateľov platieb)',
    accountCredentials: 'Prihlasovacie údaje, ak si vytvoríte účet',
    communicationPrefs: 'Komunikačné preferencie',
    automaticallyCollected: '2.2 Automaticky zhromaždené informácie',
    automaticallyCollectedText: 'Pri návšteve našej webovej stránky automaticky zhromažďujeme:',
    ipDevice: 'IP adresu a informácie o zariadení',
    browserType: 'Typ a verzia prehliadača',
    pagesVisited: 'Navštívené stránky a čas strávený na stránkach',
    referralUrls: 'Referenčné URL',
    cookiesTracking: 'Cookies a podobné technológie sledovania',
    howWeUse: '3. Ako používame vaše informácie',
    howWeUseText: 'Zhromaždené informácie používame na rôzne účely:',
    processOrders: 'Na spracovanie a vybavenie vašich objednávok',
    communicateOrders: 'Na komunikáciu s vami o vašich objednávkach, produktoch a službách',
    improveWebsite: 'Na zlepšenie našej webovej stránky a služieb',
    marketing: 'Na odosielanie marketingových komunikácií (s vaším súhlasom)',
    preventFraud: 'Na prevenciu podvodov a zvýšenie bezpečnosti',
    legalObligations: 'Na dodržiavanie zákonných povinností',
    dataSharing: '4. Zdieľanie a zverejňovanie údajov',
    dataSharingText: 'Nepredávame vaše osobné informácie. Môžeme zdieľať vaše informácie s:',
    serviceProviders: 'Poskytovateľmi služieb, ktorí vykonávajú služby v našom mene (doprava, spracovanie platieb)',
    legalAuthorities: 'Zákonnými orgánmi, keď to vyžaduje zákon',
    businessPartners: 'Obchodnými partnermi s vaším explicitným súhlasom',
    dataSecurity: '5. Bezpečnosť údajov',
    dataSecurityText: 'Implementujeme vhodné technické a organizačné opatrenia na ochranu vašich osobných informácií pred neoprávneným prístupom, zmenou, zverejnením alebo zničením. Žiadna metóda prenosu cez internet však nie je 100% bezpečná.',
    yourRights: '6. Vaše práva',
    yourRightsText: 'Máte právo:',
    accessInfo: 'Prístup k vašim osobným informáciám',
    correctInfo: 'Opraviť nepresné informácie',
    deleteInfo: 'Požiadať o vymazanie vašich informácií',
    objectProcessing: 'Namietať proti spracovaniu vašich informácií',
    dataPortability: 'Požiadať o prenosnosť údajov',
    withdrawConsent: 'Kedykoľvek odvolať súhlas',
    cookies: '7. Cookies',
    cookiesText: 'Používame cookies na zlepšenie vášho prehliadania, analýzu návštevnosti stránky a personalizáciu obsahu. Môžete ovládať cookies prostredníctvom nastavení vášho prehliadača, ale deaktivácia cookies môže ovplyvniť funkčnosť webovej stránky.',
    contactUsPrivacy: '8. Kontaktujte nás',
    contactUsPrivacyText: 'Ak máte otázky týkajúce sa týchto Zásad ochrany súkromia alebo si chcete uplatniť svoje práva, kontaktujte nás na support@ridersforge.com alebo zavolajte na +421 912 123 456.',
    
    // Refund Policy Page (SK)
    refundPolicyTitle: 'Politika vrátenia',
    refundPolicyText: 'V Riders Forge chceme, aby ste boli úplne spokojní so svojím nákupom. Ak nie ste spokojní, ponúkame jednoduchú politiku vrátenia.',
    returnEligibility: 'Nárok na vrátenie',
    returnEligibilityText: 'Aby ste mali nárok na vrátenie, váš tovar musí spĺňať nasledujúce podmienky:',
    returnCondition1: 'Tovar musí byť nenosenej, neumytý a nepoužitý',
    returnCondition2: 'Tovar musí byť v pôvodnom balení so všetkými štítkami',
    returnCondition3: 'Tovar musí byť vrátený do 30 dní od nákupu',
    returnCondition4: 'Tovar nesmie byť poškodený alebo akokoľvek upravený',
    returnProcess: 'Proces vrátenia',
    returnProcessText: 'Ak chcete vrátiť tovar, postupujte podľa týchto krokov:',
    returnStep1: 'Kontaktujte nás na support@ridersforge.com a iniciujte vrátenie',
    returnStep2: 'Poskytneme vám autorizáciu na vrátenie a pokyny na dopravu',
    returnStep3: 'Zabezpečte tovar v pôvodnom balení',
    returnStep4: 'Pošlite tovar späť k nám pomocou sledovateľnej dopravy',
    refundTimeline: 'Časový harmonogram refundácie',
    refundTimelineText: 'Po prijatí vráteného tovaru ho skontrolujeme a spracujeme vašu refundáciu do 5-7 pracovných dní. Refundácia bude vydaná na vašu pôvodnú platobnú metódu.',
    nonRefundableItems: 'Nevratné položky',
    nonRefundableItemsText: 'Nasledujúce položky nie sú vratné:',
    nonRefundable1: 'Položky, ktoré boli nosené, umyté alebo použité',
    nonRefundable2: 'Položky bez pôvodných štítkov alebo balenia',
    nonRefundable3: 'Položky vrátené po 30 dňoch od nákupu',
    refundContactText: 'Ak máte otázky týkajúce sa našej politiky vrátenia, kontaktujte nás na support@ridersforge.com alebo zavolajte na +421 912 123 456.',
    
    // Imprint Page (SK)
    companyInformation: 'Informácie o spoločnosti',
    companyName: 'Názov spoločnosti',
    responsibleForContent: 'Zodpovedný za obsah',
    responsibleForContentText: 'Obsah tejto webovej stránky spravuje a udržiava Riders Forge. Pre akékoľvek otázky týkajúce sa obsahu nás kontaktujte na support@ridersforge.com.',
    disclaimer: 'Vylúčenie zodpovednosti',
    disclaimerText: 'Informácie na tejto webovej stránke sú poskytované "tak, ako sú". V najväčšej miere dovolenej zákonom, Riders Forge vylučuje všetky vyhlásenia, záruky a podmienky týkajúce sa tejto webovej stránky a použitia tejto webovej stránky.',
    liabilityForLinks: 'Zodpovednosť za odkazy',
    liabilityForLinksText: 'Naša webová stránka môže obsahovať odkazy na externé webové stránky. Nemáme kontrolu nad povahou, obsahom a dostupnosťou týchto stránok. Zaradenie akýchkoľvek odkazov nemusí nevyhnutne znamenať odporúčanie alebo schvaľovanie názorov v nich vyjadrených.',
    copyright: 'Autorské práva',
    copyrightText: 'Všetok obsah na tejto webovej stránke, vrátane textu, grafiky, log a obrázkov, je vlastníctvom Riders Forge a je chránený autorskými právami. Neoprávnené použitie je zakázané.',
    
    // Payment Page (SK)
    acceptedPaymentMethods: 'Akceptované platobné metódy',
    acceptedPaymentMethodsText: 'Pre vaše pohodlie akceptujeme nasledujúce platobné metódy:',
    paymentMethod1: 'Kreditné karty (Visa, Mastercard, American Express)',
    paymentMethod2: 'Debetné karty',
    paymentMethod3: 'PayPal',
    paymentMethod4: 'Bankový prevod',
    paymentSecurity: 'Bezpečnosť platby',
    paymentSecurityText: 'Všetky platobné transakcie sú spracovávané bezpečne prostredníctvom šifrovaných pripojení. Neuchovávame vaše úplné informácie o kreditnej karte na našich serveroch. Vaše platobné údaje spracúvajú dôveryhodní tretí strany spracovatelia platieb.',
    paymentProcessing: 'Spracovanie platby',
    paymentProcessingText: 'Platba sa spracuje okamžite po potvrdení objednávky. Pre platby kreditnou kartou bude vaša karta účtovaná pri zadání objednávky. Pre bankové prevody prosím počítajte s 2-3 pracovnými dňami na spracovanie.',
    paymentContactText: 'Ak máte otázky týkajúce sa platobných metód alebo sa stretnete s problémami s platbou, kontaktujte nás na support@ridersforge.com alebo zavolajte na +421 912 123 456.',
    
    // Returns Page (SK)
    returnsExchanges: 'Vrátenia a výmeny',
    returnPolicy: 'Politika vrátenia',
    returnPolicyText: 'Chceme, aby ste si svoj nákup zamilovali. Ak nie ste úplne spokojní, môžete vrátiť väčšinu položiek do 30 dní od nákupu za úplný refund alebo výmenu.',
    howToReturn: 'Ako vrátiť',
    returnInstruction1: 'Prihláste sa do svojho účtu a prejdite do histórie objednávok',
    returnInstruction2: 'Vyberte položku, ktorú chcete vrátiť, a kliknite na "Vrátiť položku"',
    returnInstruction3: 'Vytlačte štítok na vrátenie a pripevnite ho na svoj balík',
    returnInstruction4: 'Pošlite položku späť k nám pomocou poskytnutého štítku na vrátenie',
    returnConditions: 'Podmienky vrátenia',
    returnsContactText: 'Pre otázky týkajúce sa vrátení alebo výmien nás kontaktujte na support@ridersforge.com alebo zavolajte na +421 912 123 456.',
    
    // Order Success Page
    orderPlacedSuccessfully: 'Objednávka bola úspešne zadaná!',
    thankYouOrder: 'Ďakujeme za vašu objednávku. Poslali sme potvrdzovací e-mail s detailmi vašej objednávky.',
    viewOrderHistory: 'Zobraziť históriu objednávok',
    
    // Hero Component
    newCollection: 'Nová kolekcia',
    from: 'Od',
    skullHoodie: 'Kápová bunda s lebkou',
    skullTee: 'Tričko s lebkou',
    gloves: 'Rukavice',
    backpack: 'Batoh',
    
    // Product Badges
    new: 'Nové',
    
    // Product Detail
    productDescriptionText: 'Prémiová kvalita {productName} z našej kolekcie {category}. Vyrobené z kvalitných materiálov s dôrazom na detail. Perfektné na každodenné nosenie alebo špeciálne príležitosti.',
    highQualityMaterials: 'Kvalitné materiály',
    comfortableFit: 'Pohodlné prispôsobenie',
    durableConstruction: 'Odolná konštrukcia',
    easyCare: 'Jednoduchá údržba',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language from localStorage or default to 'en'
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key, params = {}) => {
    let translation = translations[language]?.[key] || translations.en[key] || key;
    
    // Replace placeholders like {amount} or {count}
    if (params && typeof translation === 'string') {
      Object.keys(params).forEach(param => {
        translation = translation.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
      });
      
      // Handle pluralization for {plural}
      if (params.count !== undefined && translation.includes('{plural}')) {
        const plural = params.count === 1 ? '' : 's';
        translation = translation.replace('{plural}', plural);
      }
    }
    
    return translation;
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: [
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
    ],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

